import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  ViewChild,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { LayoutBreakPointsService } from "@shared/services/layout-break-points.service";
import { Chart } from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";

function getStackedDatapointsBody(datapoint) {
  const nonMotor = datapoint.datasetIndex % 2 !== 0;
  let line = nonMotor
    ? datapoint.dataset.data[datapoint.dataIndex].nonMotorValue
    : datapoint.dataset.data[datapoint.dataIndex].motorValue;
  const linePercentage = nonMotor
    ? datapoint.dataset.data[datapoint.dataIndex].nonMotorPercentage
    : datapoint.dataset.data[datapoint.dataIndex].motorPercentage;
  line = line.replace(":", ": <b>RM");
  line = line + " (" + linePercentage + "%)";
  return line;
}

@Component({
  selector: "app-stacked-groups-chart",
  templateUrl: "./stacked-groups-chart.component.html",
  styleUrls: ["./stacked-groups-chart.component.scss"],
  standalone: true,
  imports: [CommonModule],
})
export class StackedGroupsChartComponent
  implements OnInit, OnChanges, AfterViewInit
{
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild("stackedChartWindow", { static: false })
  private readonly stackedChartWindow: ElementRef;

  @Input() public desktopScreen;
  @Input() public dataset;
  @Input() public label;
  @Input() public yearListData;
  @Input() public refreshGraph;

  private readonly isMobile: boolean;
  private stackedChart: any;
  private stackedChart2: any;
  private readonly tooltipWidth = "250px";
  protected chartPad: any;

  constructor(
    private readonly layoutBreakPointService: LayoutBreakPointsService,
  ) {}

  ngOnInit(): void {
    this.desktopScreen = false;
    this.checkOnOrientationChanges();

    this.layoutBreakPointService
      .subscribeLayoutChanges()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.desktopScreen = this.layoutBreakPointService.renderDesktop();

        if (this.desktopScreen) {
          // if (this.stackedChart2 != null) {
          //   this.stackedChart2.destroy();
          // }
          this.createStackedChart();
        } else {
          // if (this.stackedChart != null) {
          //   this.stackedChart.destroy();
          // }
          this.createStackedChart2();
        }
      });
  }

  ngOnChanges(): void {
    if (this.refreshGraph === true) {
      this.reloadGraph();
    }
  }

  ngAfterViewInit(): void {
    this.setChartPad();
  }

  private reloadGraph(): void {
    this.checkOnOrientationChanges();

    if (this.desktopScreen) {
      if (this.stackedChart != null) {
        this.stackedChart.destroy();
        this.createStackedChart();
      }
    } else if (!this.stackedChart2) {
      if (this.stackedChart2 != null) {
        this.stackedChart2.destroy();
        this.createStackedChart2();
      }
    }
  }

  private checkOnOrientationChanges(): void {
    window.addEventListener(
      "orientationchange",
      () => {
        this.setChartPad();
      },
      false,
    );
  }

  protected getTooltipWidth(): string {
    return this.tooltipWidth;
  }

  protected getRmPosition(): number {
    function getArrayMax(array) {
      return Math.max.apply(null, array);
    }
    const findMax = this.stackedChart
      ? this.stackedChart.chart.scales["y-axis-0"].ticks
      : this.stackedChart2.chart.scales["y-axis-0"].ticks;
    const maxNum = Math.ceil(getArrayMax(findMax));

    if (!maxNum) {
      return 45;
    } else if (maxNum > 999) {
      return 44;
    } else if (maxNum > 99) {
      return 34;
    } else if (maxNum > 9) {
      return 28;
    }
  }

  private setChartPad(): void {
    if (this.desktopScreen) {
      setTimeout(() => {
        const chartWidth = this.stackedChartWindow.nativeElement.offsetWidth;
        this.chartPad =
          window.innerWidth / 2 - 5 - (chartWidth + 67.9) / 2 + "px";
      }, 1000);
    }
  }

  private createStackedChart() {
    if (this.stackedChart) {
      this.stackedChart.destroy();
    }

    let yearList = null;
    let dataset = null;

    yearList = this.yearListData;
    dataset = this.dataset;
    dataset.map((obj) => {
      obj.data.map((item, index) => {
        item.x = this.label[index];
        item.totalValue = Number(item.y);
      });
      obj.parsing = {
        yAxisKey: "totalValue",
      };
    });

    const getOrCreateLegendList = (_chart, id) => {
      const legendContainer = document.getElementById(id);
      let listContainer = null;

      if (legendContainer.childNodes.length >= 1) {
        legendContainer.lastChild.remove();
      }

      if (!listContainer) {
        listContainer = document.createElement("ul");
        listContainer.style.display = "flex";
        listContainer.style.flexDirection = "row";

        legendContainer.appendChild(listContainer);
      }
      return listContainer;
    };

    const htmlLegendPlugin = {
      id: "htmlLegend",
      afterUpdate(chart, args, _options) {
        if (args.mode === "resize" || args.mode === "attach") {
          return;
        }
        const ul = getOrCreateLegendList(chart, "stacked-legend");

        ul.style.flexDirection = "column";

        const items = chart.options.plugins.legend.labels.generateLabels(chart);

        const getLegendIndex = {
          0: 1,
          1: 0,
          2: 3,
          3: 2,
        };
        items.forEach((item, index) => {
          const legendIndex = getLegendIndex[index];
          if (index === 0 || index === 2) {
            const yearElement = document.createElement("p");
            yearElement.classList.add("stacked-legend-title");
            yearElement.classList.add("labelFont");
            yearElement.classList.add("labelYearPadding");
            const yearText = document.createTextNode(
              index === 0 ? yearList["prevYear"] : yearList["thisYear"],
            );
            yearElement.appendChild(yearText);
            ul.appendChild(yearElement);
          }

          item.pointStyle = "circle";
          const li = document.createElement("li");
          li.style.alignItems = "center";
          li.style.cursor = "pointer";
          li.style.display = "flex";
          li.style.flexDirection = "row";
          li.classList.add("stacked-legend-li");
          li.classList.add("stacked-year-content");

          const boxSpan = document.createElement("span");
          boxSpan.style.background = items[legendIndex].fillStyle;
          boxSpan.style.borderColor = items[legendIndex].strokeStyle;
          boxSpan.style.borderWidth = items[legendIndex].lineWidth + "px";
          boxSpan.style.borderRadius = "100%";
          boxSpan.style.display = "inline-block";
          boxSpan.style.height = "12px";
          boxSpan.style.width = "13.21px";
          boxSpan.style.marginRight = "6.5px";

          const textContainer = document.createElement("p");
          textContainer.style.color = items[legendIndex].fontColor;

          const text = document.createTextNode(items[legendIndex].text);

          textContainer.appendChild(text);

          li.appendChild(boxSpan);
          li.appendChild(textContainer);
          ul.appendChild(li);
        });
      },
    };

    const toolTipVerticalLine = {
      id: "toolTipVerticalLine",
      afterDatasetsDraw: function (context, _args, _options) {
        const tooltipModel = context.tooltip;
        const visibleDataSetCount = context.getVisibleDatasetCount();

        if (tooltipModel?.opacity === 1) {
          for (let i = 0; i < visibleDataSetCount; i++) {
            const meta = context.getDatasetMeta(i);

            let bottomY = null,
              ctx = stackedChart.ctx,
              x = null,
              topY = null;
            const y_axis = stackedChart.scales.y;

            meta.data.forEach((item) => {
              if (item.active === true) {
                x = item.x;
                topY = y_axis.top - 45;
                bottomY = item.y;
              }
            });

            ctx.save();
            ctx.beginPath();
            ctx.moveTo(x, topY);
            ctx.lineTo(x, bottomY);
            ctx.lineWidth = 2;
            ctx.strokeStyle = "#07C";
            ctx.stroke();
            ctx.restore();
            ctx.closePath();
          }
        }
      },
    };

    const yScaleLabel = {
      id: "yScaleLabel",
      afterDatasetsDraw(chart, _args, _options) {
        const {
          ctx,
          chartArea: { top },
        } = chart;

        const arr = chart.data.datasets.flatMap((item) =>
          item.data.map((val) => val),
        );
        let hideText = false;
        if (arr.includes(0)) {
          hideText = true;
        }

        ctx.save();
        ctx.font = "bold 12px Helvetica Neue, Helvetica, Arial, sans-serif";
        if (!hideText) {
          ctx.fillText("RM (K)", 45, top - 25);
        }
        ctx.restore();
      },
    };

    const ctx = document.getElementById("stackedChart") as HTMLCanvasElement;
    const stackedChart = new Chart(ctx, {
      type: "bar",
      plugins: [
        ChartDataLabels,
        htmlLegendPlugin,
        toolTipVerticalLine,
        yScaleLabel,
      ],
      data: {
        datasets: dataset,
      },
      options: {
        parsing: {
          xAxisKey: "x",
          yAxisKey: "totalValue",
        },
        scales: {
          x: {
            display: true,
            stacked: true,
            grid: {
              drawOnChartArea: false,
              drawTicks: false,
            },
            ticks: {
              padding: 0,
              font: () => {
                return {
                  size: 12,
                  family: "Allianz Neo , Arial, sans-serif",
                };
              },
            },
          },
          y: {
            display: true,
            stacked: true,
            grid: {
              drawOnChartArea: false,
            },
            ticks: {
              callback: function (label, _index, _labels) {
                return Intl.NumberFormat().format(Number(label));
              },
              padding: 5,
              font: () => {
                return {
                  size: 12,
                  family: "Allianz Neo , Arial, sans-serif",
                };
              },
            },
          },
        },
        layout: {
          padding: {
            left: 0,
            right: 0,
            top: this.desktopScreen ? 120 : 40,
            bottom: 0,
          },
        },
        maintainAspectRatio: false,
        responsive: true,
        plugins: {
          datalabels: {
            display: false,
          },
          legend: {
            display: false,
          },
          tooltip: {
            mode: "x",
            enabled: false,
            external: function (context) {
              let tooltipEl = document.getElementById("stacked-tooltip");
              if (!tooltipEl) {
                tooltipEl = document.createElement("div");
                tooltipEl.id = "stacked-tooltip";
                tooltipEl.innerHTML = "<div></div>";
                document.body.appendChild(tooltipEl);
              }
              window.addEventListener("click", function () {});

              tooltipEl.style.position = "relative";
              const tooltipModel = context.tooltip;
              if (!tooltipModel || tooltipModel.opacity === 0) {
                tooltipEl.style.opacity = "0";
                return;
              }

              tooltipEl.classList.remove("above", "below", "no-transform");
              if (tooltipModel.yAlign) {
                tooltipEl.classList.add(tooltipModel.yAlign);
              } else {
                tooltipEl.classList.add("no-transform");
              }

              if (tooltipModel.body) {
                let titleYear;
                const stackIndex = Number(
                  tooltipModel.dataPoints[0].dataset.stack,
                );
                if (stackIndex === 1) {
                  titleYear = yearList.prevYear;
                } else {
                  titleYear = yearList.thisYear;
                }
                const caretX = tooltipModel.caretX / 2;
                const titleLines = tooltipModel.title || [];

                const datapoints = tooltipModel.dataPoints.map(
                  getStackedDatapointsBody,
                );
                const tooltipContainerStyles =
                  "display: inline-flex !important; justify-content: space-around; width: 100%; align-items: center;";
                let innerHtml =
                  '<div class="stacked-tooltip-container" style="' +
                  tooltipContainerStyles +
                  '">';

                let motorValue =
                  tooltipModel.dataPoints[stackIndex - 1].raw["motorValue"];
                motorValue = parseFloat(
                  motorValue.split(": ")[1].replace(/,/g, ""),
                );
                let nonMotorValue =
                  tooltipModel.dataPoints[stackIndex - 1].raw["nonMotorValue"];
                nonMotorValue = parseFloat(
                  nonMotorValue.split(": ")[1].replace(/,/g, ""),
                );
                const sum = (motorValue + nonMotorValue).toLocaleString(
                  "en-US",
                );

                titleLines.forEach(function (title) {
                  innerHtml +=
                    '<div class="stacked-tooltip-total labelFont" style="padding-left: 10px; padding-right: 10px">Total GWP for ' +
                    title +
                    " " +
                    titleYear +
                    ": " +
                    "<b> RM " +
                    sum +
                    "</b></div>";
                });
                datapoints.forEach((datapoint) => {
                  innerHtml +=
                    '<div class="stacked-tooltip-text labelFont" style="padding-left: 10px; padding-right: 10px">' +
                    datapoint +
                    "</b></div>";
                });

                innerHtml +=
                  '</div> <div class="vertical-line" style="border-left: 2px solid black; height: 0px; position:relative; top: 72px; right:calc(50% - ' +
                  caretX +
                  'px + 120px);"></div>';

                tooltipEl.innerHTML = innerHtml;
              }

              tooltipEl.style.opacity = "1";

              const chartEl: any = document.getElementsByClassName(
                "stacked-chart-container",
              );
              const chartWidth = chartEl[1].offsetWidth;

              const canvasEl: any =
                document.getElementsByClassName("chart-and-legend");
              const canvasWidth = canvasEl[0].offsetWidth;
              const legendWidth = canvasWidth - chartWidth;
              const width = canvasWidth - legendWidth - 50;
              tooltipEl.style.width = width + "px";

              window.addEventListener("resize", function () {
                const chartEl2: any = document.getElementsByClassName(
                  "stacked-chart-container",
                );
                const chartWidth2 = chartEl2[1].offsetWidth;

                const canvasEl2: any =
                  document.getElementsByClassName("chart-and-legend");
                const canvasWidth2 = canvasEl2[0].offsetWidth;
                const legendWidth2 = canvasWidth2 - chartWidth2;
                const width2 = canvasWidth2 - legendWidth2;
                tooltipEl.style.width = width2 + "px";
              });

              tooltipEl.style.left = "2%";
              tooltipEl.style.boxShadow = "0px 2px 4px #41414180";
              tooltipEl.style.border = "solid #006192";
              tooltipEl.style.borderWidth = "1px";
              tooltipEl.style.display = "inline-flex";
              tooltipEl.style.justifyContent = "space-around";
              tooltipEl.style.height = "72px";
              tooltipEl.style.backgroundColor = "#FFF";
            },
          },
        },
        animation: {
          duration: 1000,
        },
        elements: {
          line: {
            fill: false,
            tension: 0,
          },
        },
      },
    });
    this.stackedChart = stackedChart;
    return this.stackedChart;
  }

  private createStackedChart2() {
    if (this.stackedChart2) {
      this.stackedChart2.destroy();
    }
    const yearList = this.yearListData;
    const dataset = this.dataset;
    dataset.map((obj) => {
      obj.data.map((item, index) => {
        item.x = this.label[index];
        item.totalValue = Number(item.y);
      });
      obj.parsing = {
        yAxisKey: "totalValue",
      };
    });

    const getOrCreateLegendList = (_chart, id) => {
      const legendContainer = document.getElementById(id);
      legendContainer.style.display = "flex";
      legendContainer.style.flexDirection = "row";
      let listContainer = null;
      if (legendContainer.childNodes.length >= 1) {
        legendContainer.lastChild.remove();
      }

      if (!listContainer) {
        listContainer = document.createElement("div");
        listContainer.style.display = "flex";
        listContainer.style.flexDirection = "row";
        listContainer.style.paddingTop = "10px";

        listContainer.classList.add(`${_chart.canvas.id}-legend2`);

        legendContainer.appendChild(listContainer);
      }
      return listContainer;
    };

    const htmlLegendPlugin = {
      id: "htmlLegend",
      afterUpdate(chart, args, _options) {
        if (args.mode === "resize" || args.mode === "attach") {
          return;
        }
        const div = getOrCreateLegendList(chart, "stacked-legend2");

        const items = chart.options.plugins.legend.labels.generateLabels(chart);

        const getLegendIndex = {
          0: 1,
          1: 0,
          2: 3,
          3: 2,
        };
        let yearElement = null;
        items.forEach((item, index) => {
          const legendIndex = getLegendIndex[index];
          if (index === 0 || index === 2) {
            yearElement = document.createElement("div");
            yearElement.classList.add("stacked-legend-title");
            yearElement.style.display = "flex";
            yearElement.style.flexDirection = "column";
            yearElement.style.minWidth = "120px";

            const yearText = document.createElement("p");
            const text = document.createTextNode(
              index === 0 ? yearList["prevYear"] : yearList["thisYear"],
            );
            yearText.style.paddingBottom = "12px";
            yearText.appendChild(text);

            yearElement.appendChild(yearText);
            div.appendChild(yearElement);
          }
          item.pointStyle = "circle";
          const p = document.createElement("p");
          p.style.alignItems = "center";
          p.style.cursor = "pointer";
          p.classList.add("stacked-legend-li");
          p.classList.add("mobile-stacked-legend-li");
          yearElement.appendChild(p);

          const boxSpan = document.createElement("span");
          boxSpan.classList.add("mobile-stacked-legend-li-span");
          boxSpan.style.background = items[legendIndex].fillStyle;
          boxSpan.style.borderColor = items[legendIndex].strokeStyle;
          boxSpan.style.borderWidth = items[legendIndex].lineWidth + "px";
          boxSpan.style.borderRadius = "100%";
          boxSpan.style.display = "inline-block";
          boxSpan.style.height = "13px";
          boxSpan.style.width = "13px";
          boxSpan.style.marginRight = "10px";

          const textContainer = document.createElement("label");
          textContainer.style.fontSize = "10px";
          textContainer.style.color = items[legendIndex].fontColor;

          const text = document.createTextNode(items[legendIndex].text);

          textContainer.appendChild(text);

          p.appendChild(boxSpan);
          p.appendChild(textContainer);
        });
      },
    };

    const toolTipVerticalLine = {
      id: "toolTipVerticalLine",
      afterDatasetsDraw: function (context, _args, _options) {
        const tooltipModel = context.tooltip;
        const visibleDataSetCount = context.getVisibleDatasetCount();

        if (tooltipModel?.opacity === 1) {
          for (let i = 0; i < visibleDataSetCount; i++) {
            const meta = context.getDatasetMeta(i);

            let bottomY = null,
              ctx = stackedChart2.ctx,
              x = null,
              topY = null;
            const y_axis = stackedChart2.scales.y;

            meta.data.forEach((item) => {
              if (item.active === true) {
                x = item.x;
                topY = y_axis.top - 45;
                bottomY = item.y;
              }
            });

            ctx.save();
            ctx.beginPath();
            ctx.moveTo(x, topY);
            ctx.lineTo(x, bottomY);
            ctx.lineWidth = 2;
            ctx.strokeStyle = "#07C";
            ctx.stroke();
            ctx.restore();
            ctx.closePath();
          }
        }
      },
    };

    const yScaleLabel = {
      id: "yScaleLabel",
      afterDatasetsDraw(chart, _args, _options) {
        const {
          ctx,
          chartArea: { top },
        } = chart;
        ctx.save();
        ctx.font = "700 12px Arial, sans-serif";
        ctx.fillText("RM (K)", 40, top - 25);
        ctx.restore();
      },
    };

    const ctx2 = document.getElementById("stackedChart2") as HTMLCanvasElement;
    const stackedChart2 = new Chart(ctx2, {
      type: "bar",
      plugins: [
        ChartDataLabels,
        htmlLegendPlugin,
        toolTipVerticalLine,
        yScaleLabel,
      ],
      data: {
        datasets: dataset,
      },
      options: {
        parsing: {
          xAxisKey: "x",
          yAxisKey: "totalValue",
        },
        scales: {
          x: {
            display: true,
            stacked: true,
            grid: {
              drawOnChartArea: false,
              drawTicks: false,
            },
          },
          y: {
            display: true,
            stacked: true,
            grid: {
              drawOnChartArea: false,
            },
            ticks: {
              callback: function (label, _index, _labels) {
                return Intl.NumberFormat().format(Number(label));
              },
            },
          },
        },
        layout: {
          padding: {
            left: 0,
            right: 0,
            top: this.desktopScreen ? 120 : 40,
            bottom: 0,
          },
        },
        maintainAspectRatio: false,
        responsive: true,
        plugins: {
          datalabels: {
            display: false,
          },
          legend: {
            display: false,
          },
          tooltip: {
            mode: "x",
            enabled: false,
            external: function (context) {
              let tooltipEl = document.getElementById("stacked-tooltip2");
              if (!tooltipEl) {
                tooltipEl = document.createElement("div");
                tooltipEl.id = "stacked-tooltip2";
                tooltipEl.innerHTML = "<div></div>";
                document.body.appendChild(tooltipEl);
              }
              window.addEventListener("click", function () {});

              tooltipEl.style.position = "relative";
              const tooltipModel = context.tooltip;
              if (!tooltipModel || tooltipModel.opacity === 0) {
                tooltipEl.style.opacity = "0";
                return;
              }

              tooltipEl.classList.remove("above", "below", "no-transform");
              if (tooltipModel.yAlign) {
                tooltipEl.classList.add(tooltipModel.yAlign);
              } else {
                tooltipEl.classList.add("no-transform");
              }

              if (tooltipModel.body) {
                let titleYear;
                const stackIndex = Number(
                  tooltipModel.dataPoints[0].dataset.stack,
                );
                if (stackIndex === 1) {
                  titleYear = yearList.prevYear;
                } else {
                  titleYear = yearList.thisYear;
                }
                const caretX = tooltipModel.caretX / 2;
                const titleLines = tooltipModel.title || [];

                const datapoints = tooltipModel.dataPoints.map(
                  getStackedDatapointsBody,
                );
                const tooltipContainerStyles =
                  "display: inline-flex !important; justify-content: space-around; width: 100%; align-items: center;";
                let innerHtml =
                  '<div class="stacked-tooltip-container" style="' +
                  tooltipContainerStyles +
                  '">';

                let motorValue =
                  tooltipModel.dataPoints[stackIndex - 1].raw["motorValue"];
                motorValue = parseFloat(
                  motorValue.split(": ")[1].replace(/,/g, ""),
                );
                let nonMotorValue =
                  tooltipModel.dataPoints[stackIndex - 1].raw["nonMotorValue"];
                nonMotorValue = parseFloat(
                  nonMotorValue.split(": ")[1].replace(/,/g, ""),
                );
                const sum = (motorValue + nonMotorValue).toLocaleString(
                  "en-US",
                );

                titleLines.forEach(function (title) {
                  innerHtml +=
                    '<div class="stacked-tooltip-total labelFont" style="padding-left: 10px; padding-right: 10px">Total GWP for ' +
                    title +
                    " " +
                    titleYear +
                    ": " +
                    "<b> RM " +
                    sum +
                    "</b></div>";
                });
                datapoints.forEach((datapoint) => {
                  innerHtml +=
                    '<div class="stacked-tooltip-text labelFont" style="padding-left: 10px; padding-right: 10px">' +
                    datapoint +
                    "</b></div>";
                });

                innerHtml +=
                  '</div> <div class="vertical-line" style="border-left: 2px solid black; height: 0px; position:relative; top: 72px; right:calc(50% - ' +
                  caretX +
                  'px + 120px);"></div>';

                tooltipEl.innerHTML = innerHtml;
              }

              tooltipEl.style.opacity = "1";

              const chartEl: any = document.getElementsByClassName(
                "stacked-chart-container",
              );
              const chartWidth = chartEl[1].offsetWidth;

              const canvasEl: any =
                document.getElementsByClassName("chart-and-legend");
              const canvasWidth = canvasEl[0].offsetWidth;
              const legendWidth = canvasWidth - chartWidth;
              const width = canvasWidth - legendWidth - 50;
              tooltipEl.style.width = width + "px";

              window.addEventListener("resize", function () {
                const chartEl2: any = document.getElementsByClassName(
                  "stacked-chart-container",
                );
                const chartWidth2 = chartEl2[1].offsetWidth;

                const canvasEl2: any =
                  document.getElementsByClassName("chart-and-legend");
                const canvasWidth2 = canvasEl2[0].offsetWidth;
                const legendWidth2 = canvasWidth2 - chartWidth2;
                const width2 = canvasWidth2 - legendWidth2;
                tooltipEl.style.width = width2 + "px";
              });

              tooltipEl.style.left = "2%";
              tooltipEl.style.boxShadow = "0px 2px 4px #41414180";
              tooltipEl.style.border = "solid #006192";
              tooltipEl.style.borderWidth = "1px";
              tooltipEl.style.display = "inline-flex";
              tooltipEl.style.justifyContent = "space-around";
              tooltipEl.style.height = "72px";
              tooltipEl.style.backgroundColor = "#FFF";
            },
          },
        },
        animation: {
          duration: 1000,
        },
        elements: {
          line: {
            fill: false,
            tension: 0,
          },
        },
      },
    });
    this.stackedChart2 = stackedChart2;
    return this.stackedChart2;
  }

  protected setMobileStackedWidth(): string {
    const numberOfMonths = this.label.length;
    const width = 70 * numberOfMonths + 122;
    const returnValue = width + "px";
    return returnValue;
  }

  protected setMobileScrollWidth(): number {
    const chartAndLegend: any =
      document.getElementsByClassName("chart-and-legend");
    const width = chartAndLegend[0].offsetWidth;
    return width;
  }
}
